// src/components/KasrDaryaftList.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { translateApiMessage } from "../utils/formErrors";
import axiosInstance from "../utils/axiosInstance";
import Select from "./ui/Select";
import PaginationControls from "../components/PaginationControls";
import {
  Search,
  Pencil,
  Trash2,
  FileText,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Plus,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchCostumers } from "../actions/authActions";
import EditKasrDaryaftModal from "./KasrDaryaftModal";
import { formatNumber } from "../utils/numberFormat";

/* ---------- helpers ---------- */
const cx = (...classes) => classes.filter(Boolean).join(" ");
const firstErr = (v) => (Array.isArray(v) ? v[0] : v);
const extractErrorData = (axiosError) =>
  axiosError?.response?.data ?? {
    detail: axiosError?.message || "خطای نامشخص",
  };

const KasrDaryaftList = () => {
  const dispatch = useDispatch();
  const {
    costumerList = [],
    customersLoading,
    customersError,
  } = useSelector((s) => s.costumers || {});

  // list state
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // filters
  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");
  const [isPaidFilter, setIsPaidFilter] = useState("");
  const [isAppealedFilter, setIsAppealedFilter] = useState("");

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // modal state
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState({});

  const boolOptions = useMemo(
    () => [
      { value: "", label: "همه" },
      { value: "True", label: "بله" },
      { value: "False", label: "خیر" },
    ],
    []
  );

  // customer options (MUST be top-level hook, not inside JSX)
  const customerOptions = useMemo(
    () =>
      (costumerList || []).map((c) => ({
        value: String(c.id),
        label: c.full_name,
      })),
    [costumerList]
  );

  useEffect(() => {
    dispatch(fetchCostumers());
  }, [dispatch]);

  // debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(searchText);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page", String(currentPage));
    params.set("page_size", String(pageSize));

    if (query?.trim()) params.set("search", query.trim());
    if (isPaidFilter !== "") params.set("is_paid", String(isPaidFilter));
    if (isAppealedFilter !== "")
      params.set("is_appealed", String(isAppealedFilter));

    return params;
  }, [currentPage, pageSize, query, isPaidFilter, isAppealedFilter]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = buildParams();
      const url = `/kasrdaryafts/${params.toString() ? `?${params}` : ""}`;
      const { data } = await axiosInstance.get(url);

      const results = Array.isArray(data?.results) ? data.results : data || [];
      setItems(results);
      setCount(data?.count ?? results.length);
      setNext(data?.next ?? null);
      setPrevious(data?.previous ?? null);
    } catch (err) {
      setItems([]);
      setCount(0);
      setNext(null);
      setPrevious(null);

      const msg =
        translateApiMessage(err?.response?.data?.detail) ||
        "خطا در دریافت لیست کسر دریافت‌ها";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  const hasNext = !!next;
  const hasPrevious = !!previous;

  const handlePageChange = (e) => setCurrentPage(Number(e.target.value));
  const handleNextPage = () => hasNext && setCurrentPage((p) => p + 1);
  const handlePreviousPage = () => hasPrevious && setCurrentPage((p) => p - 1);
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("آیا از حذف این کسر دریافت اطمینان دارید؟")) return;

    try {
      await axiosInstance.delete(`/kasrdaryafts/${id}/`);
      await fetchList();
    } catch (err) {
      const msg =
        translateApiMessage(err?.response?.data?.detail) || "خطا در حذف رکورد";
      setError(msg);
    }
  };

  const openEdit = (item) => {
    setServerErrors({});
    setError("");
    setEditingItem(item);

    const declaredToId =
      item?.declared_to?.id ?? item?.declared_to ?? item?.declared_to_id ?? "";
    const customerId =
      item?.customer?.id ?? item?.customer ?? item?.customer_id ?? "";

    const cottageNumber =
      item?.cottage?.cottage_number ??
      item?.cottage ??
      item?.cottage_number ??
      "";

    setEditFormData({
      id: item.id,
      declared_to: String(declaredToId || ""),
      customer: String(customerId || ""),
      date: item.date ?? "",
      cottage: String(cottageNumber || ""),
      appeal_deadline: item.appeal_deadline ?? "",
      total_value: item.total_value ?? "",
      is_appealed: !!item.is_appealed,
      is_paid: !!item.is_paid,
      document: null,
    });

    setIsEditOpen(true);
  };

  const closeEdit = () => {
    setIsEditOpen(false);
    setEditingItem(null);
    setEditFormData({});
    setIsSaving(false);
    setServerErrors({});
  };

  const handleEditPlainChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "file") {
      setEditFormData((p) => ({ ...p, [name]: files?.[0] || null }));
      return;
    }

    setEditFormData((p) => ({
      ...p,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingItem?.id) return;

    setError("");
    setServerErrors({});

    const data = new FormData();
    data.append("declared_to", editFormData.declared_to || "");
    data.append("customer", editFormData.customer || "");
    data.append("date", editFormData.date || "");
    data.append("cottage", editFormData.cottage || "");
    data.append("appeal_deadline", editFormData.appeal_deadline || "");
    data.append("total_value", String(editFormData.total_value ?? ""));
    data.append("is_appealed", editFormData.is_appealed ? "true" : "false");
    data.append("is_paid", editFormData.is_paid ? "true" : "false");
    if (editFormData.document instanceof File)
      data.append("document", editFormData.document);

    try {
      setIsSaving(true);
      await axiosInstance.patch(`/kasrdaryafts/${editingItem.id}/`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      closeEdit();
      await fetchList();
    } catch (err) {
      const errData = extractErrorData(err);
      setServerErrors(errData);

      const msg =
        errData?.detail ||
        firstErr(errData?.non_field_errors) ||
        "خطا در ذخیره تغییرات";
      setError(firstErr(msg));
      setIsSaving(false);
    }
  };

  // minimal select classNames (not hooks)
  const smallSelectClassNames = {
    control: (state) =>
      cx(
        "min-h-[42px] rounded-xl border px-2 bg-white text-slate-900 shadow-sm ",
        state.isFocused
          ? "border-sky-500 ring-2 ring-sky-100"
          : "border-slate-200"
      ),
    menu: () =>
      "rounded-xl border border-slate-200 shadow-lg overflow-hidden bg-white",
    option: (state) =>
      cx(
        "px-3 py-2 text-sm cursor-pointer",
        state.isFocused ? "bg-slate-100" : "bg-white",
        state.isSelected ? "bg-sky-50" : ""
      ),
    singleValue: () => "text-sm",
    placeholder: () => "text-sm text-slate-400",
    input: () => "text-sm",
    indicatorSeparator: () => "bg-slate-200",
    dropdownIndicator: () => "text-slate-500",
    clearIndicator: () => "text-slate-500",
  };

  const rowStartIndex = (currentPage - 1) * pageSize;

  const displayCustomer = (item) => item?.customer_name || "—";
  const displayDeclaredTo = (item) => item?.declared_to_name || "—";
  const displayCottage = (v) => {
    if (!v) return "";
    if (typeof v === "string" || typeof v === "number") return String(v);
    return String(v.cottage_number || v.number || v.id || "");
  };

  return (
    <div className="mt-24 w-full" dir="rtl">
      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header */}
        <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900">
              لیست کسر دریافت‌ها
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              مجموع: <span className="font-medium text-slate-700">{count}</span>
            </p>
          </div>

          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <Link
              to="/add-kasr-daryaft"
              className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-sky-700"
            >
              <Plus size={18} />
              افزودن کسری دریافتی
            </Link>

            {/* Search */}
            <div className="relative w-full sm:w-[380px]">
              <Search
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                size={18}
              />
              <input
                type="text"
                placeholder="جستجو: کوتاژ / مشتری / ابلاغ شده به"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pr-10 pl-3 text-sm text-slate-900 shadow-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              />
            </div>

            {/* is_paid */}
            <div className="w-full sm:w-[180px]">
              <Select
                unstyled
                classNames={smallSelectClassNames}
                value={
                  boolOptions.find((o) => o.value === isPaidFilter) ||
                  boolOptions[0]
                }
                onChange={(opt) => {
                  setIsPaidFilter(opt ? opt.value : "");
                  setCurrentPage(1);
                }}
                options={boolOptions}
                isClearable={false}
                isDisabled={loading}
              />
            </div>

            {/* is_appealed */}
            <div className="w-full sm:w-[180px]">
              <Select
                unstyled
                classNames={smallSelectClassNames}
                value={
                  boolOptions.find((o) => o.value === isAppealedFilter) ||
                  boolOptions[0]
                }
                onChange={(opt) => {
                  setIsAppealedFilter(opt ? opt.value : "");
                  setCurrentPage(1);
                }}
                options={boolOptions}
                isClearable={false}
                isDisabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Errors */}
        {customersError ? (
          <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            خطا در دریافت لیست مشتریان
          </div>
        ) : null}

        {loading && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
            در حال بارگذاری...
          </div>
        )}

        {error && !loading && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            خطا: {error}
          </div>
        )}

        {!loading && !error && (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-slate-200 px-3 py-3 sm:px-4">
              <PaginationControls
                currentPage={currentPage}
                totalPages={Math.max(1, Math.ceil(count / pageSize))}
                pageSize={pageSize}
                pageSizeOptions={[10, 20, 50, 100, 200]}
                hasNext={!!next}
                hasPrevious={!!previous}
                onPageChange={handlePageChange}
                onNextPage={handleNextPage}
                onPreviousPage={handlePreviousPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>

            {items.length === 0 ? (
              <div className="p-6 text-sm text-slate-600">
                موردی با این جستجو/فیلترها یافت نشد.
              </div>
            ) : (
              <div className="w-full overflow-auto">
                <table className="w-full min-w-[1180px] text-sm">
                  <thead className="sticky top-0 z-0 bg-slate-50 text-slate-700">
                    <tr className="border-b border-slate-200">
                      <th className="px-3 py-3 text-right font-semibold">
                        ردیف
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        مشتری
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        ابلاغ شده به
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        کوتاژ
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        مبلغ کل
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        تاریخ
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        مهلت اعتراض
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        اعتراض
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        پرداخت
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        فایل
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        عملیات
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {items.map((item, idx) => (
                      <tr
                        key={item.id}
                        className="border-b border-slate-100 hover:bg-slate-50/60"
                      >
                        <td className="px-3 py-3 text-slate-700">
                          {rowStartIndex + idx + 1}
                        </td>

                        <td className="px-3 py-3 text-slate-700">
                          {displayCustomer(item)}
                        </td>

                        <td className="px-3 py-3 text-slate-700">
                          {displayDeclaredTo(item)}
                        </td>

                        <td className="px-3 py-3 text-blue-900 underline font-medium">
                          <Link to={`/cottages/${item.cottage}`}>
                            {displayCottage(item.cottage)}
                          </Link>
                        </td>

                        <td className="px-3 py-3 text-slate-700">
                          {formatNumber(item.total_value)}
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          {item.date}
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          {item.appeal_deadline}
                        </td>

                        <td className="px-3 py-3">
                          {item.is_appealed ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-amber-700">
                              <AlertTriangle size={16} /> بله
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2.5 py-1 text-slate-700">
                              <XCircle size={16} /> خیر
                            </span>
                          )}
                        </td>

                        <td className="px-3 py-3">
                          {item.is_paid ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700">
                              <CheckCircle2 size={16} /> بله
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-rose-700">
                              <XCircle size={16} /> خیر
                            </span>
                          )}
                        </td>

                        <td className="px-3 py-3">
                          {item.document ? (
                            <a
                              href={item.document}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 hover:bg-slate-50"
                            >
                              <FileText size={16} /> مشاهده
                            </a>
                          ) : (
                            <span className="text-slate-400">بدون فایل</span>
                          )}
                        </td>

                        <td className="px-3 py-3">
                          <div className="flex items-center justify-start gap-2">
                            <button
                              type="button"
                              onClick={() => openEdit(item)}
                              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-slate-700 hover:bg-slate-50"
                            >
                              <Pencil size={16} /> ویرایش
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDelete(item.id)}
                              className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-rose-700 hover:bg-rose-100"
                            >
                              <Trash2 size={16} /> حذف
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="border-t border-slate-200 px-3 py-3 sm:px-4">
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                pageSizeOptions={[10, 20, 50, 100, 200]}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                onPageChange={handlePageChange}
                onNextPage={handleNextPage}
                onPreviousPage={handlePreviousPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </div>
        )}
      </div>

      <EditKasrDaryaftModal
        open={isEditOpen}
        loading={isSaving}
        item={editingItem}
        form={editFormData}
        setForm={setEditFormData}
        serverErrors={serverErrors}
        customersLoading={customersLoading}
        customersError={customersError}
        customerOptions={customerOptions}
        onClose={closeEdit}
        onPlainChange={handleEditPlainChange}
        onSave={handleSaveEdit}
      />
    </div>
  );
};

export default KasrDaryaftList;
